import React, { useMemo, useState } from 'react';
import { Button, Card, Col, Row, Select, Space, Statistic, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { useEmployeeOptions } from './shared/useEmployeeOptions';
import { hrmsStatusColor } from './shared/hrmsStatus';

const { Text } = Typography;

const AssetCustodyPage: React.FC = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<string>('Issued');
  const { empLabel } = useEmployeeOptions();

  const params = statusFilter ? { status: statusFilter } : undefined;
  const { data: rows = [], isLoading } = useQuery({
    queryKey: hrmsKeys.assetIssues(params),
    queryFn: () => hrmsService.listAssetIssues(params),
  });

  const stats = useMemo(() => {
    const issued = rows.filter((r: any) => r.status === 'Issued').length;
    const returned = rows.filter((r: any) => r.status === 'Returned').length;
    return { total: rows.length, issued, returned };
  }, [rows]);

  const columns: ColumnsType<any> = [
    {
      title: 'Issue #',
      dataIndex: 'issue_number',
      width: 120,
      fixed: 'left',
      render: (v: string, r) => (
        <a onClick={() => navigate(`/hr/asset-custody/${r.id}`)}>{v || 'Open'}</a>
      ),
    },
    { title: 'Employee', dataIndex: 'employee_id', ellipsis: true, render: (id: string) => empLabel(id) },
    { title: 'Asset code', dataIndex: 'asset_code', width: 120 },
    { title: 'Asset name', dataIndex: 'asset_name' },
    {
      title: 'Issued',
      dataIndex: 'issue_date',
      width: 120,
      render: (d: string) => (d ? dayjs(d).format('DD-MMM-YYYY') : '—'),
    },
    {
      title: 'Returned',
      dataIndex: 'return_date',
      width: 120,
      render: (d: string) => (d ? dayjs(d).format('DD-MMM-YYYY') : '—'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 110,
      render: (s: string) => <Tag color={hrmsStatusColor(s)}>{s}</Tag>,
    },
    {
      title: 'Actions',
      key: 'a',
      fixed: 'right',
      width: 90,
      render: (_, r) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => navigate(`/hr/asset-custody/${r.id}`)}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Asset custody"
          subtitle="Issue and return company assets to employees"
          primaryActions={
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/hr/asset-custody/new')}>
              Issue asset
            </Button>
          }
        />
      }
    >
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={8}><Card size="small"><Statistic title="Shown" value={stats.total} /></Card></Col>
        <Col xs={12} sm={8}><Card size="small"><Statistic title="Issued" value={stats.issued} /></Card></Col>
        <Col xs={24} sm={8}><Card size="small"><Statistic title="Returned" value={stats.returned} /></Card></Col>
      </Row>
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space>
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 140 }}
            value={statusFilter}
            onChange={setStatusFilter}
            options={['Issued', 'Returned', 'Lost', 'WrittenOff'].map((v) => ({ value: v, label: v }))}
          />
          <Text type="secondary">{rows.length} record(s)</Text>
        </Space>
      </Card>
      <Table
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={rows}
        scroll={{ x: 1000 }}
        pagination={{ pageSize: 20 }}
        onRow={(r) => ({
          onClick: () => navigate(`/hr/asset-custody/${r.id}`),
          style: { cursor: 'pointer' },
        })}
      />
    </WorkspaceLayout>
  );
};

export default AssetCustodyPage;
